import { useState, useEffect, useCallback } from 'react';
import { holdCall, resumeCall, muteCall, unmuteCall, endCall } from '../services/cxone-sdk.service';
import { logger } from '../utils/logger';

interface UseCallControlsOptions {
  contactId: string | null;
  initialMuted: boolean;
  onCallEnd: () => void;
}

export function useCallControls({ contactId, initialMuted, onCallEnd }: UseCallControlsOptions) {
  const [isMuted, setIsMuted] = useState(initialMuted);
  const [isLoading, setIsLoading] = useState<string | null>(null);

  useEffect(() => {
    setIsMuted(initialMuted);
  }, [initialMuted]);

  const handleHold = useCallback(async () => {
    if (!contactId) {
      logger.warn('[CallCard] Cannot hold: no contactId');
      return;
    }

    setIsLoading('hold');
    try {
      await holdCall(contactId);
    } catch (error) {
      logger.error('[CallCard] Hold failed:', error);
    } finally {
      setIsLoading(null);
    }
  }, [contactId]);

  const handleResume = useCallback(async () => {
    if (!contactId) {
      logger.warn('[CallCard] Cannot resume: no contactId');
      return;
    }

    setIsLoading('resume');
    try {
      await resumeCall(contactId);
    } catch (error) {
      logger.error('[CallCard] Resume failed:', error);
    } finally {
      setIsLoading(null);
    }
  }, [contactId]);

  const handleMute = useCallback(async () => {
    if (!contactId) {
      logger.warn('[CallCard] Cannot mute: no contactId');
      return;
    }

    setIsLoading('mute');
    try {
      if (isMuted) {
        await unmuteCall(contactId);
      } else {
        await muteCall(contactId);
      }
      setIsMuted(!isMuted);
    } catch (error) {
      logger.error('[CallCard] Mute toggle failed:', error);
    } finally {
      setIsLoading(null);
    }
  }, [contactId, isMuted]);

  const handleEnd = useCallback(async () => {
    if (!contactId) {
      return;
    }

    setIsLoading('end');
    try {
      await endCall(contactId);
      onCallEnd();
    } catch (error) {
      logger.error('[CallCard] End call failed:', error);
      onCallEnd();
    } finally {
      setIsLoading(null);
    }
  }, [contactId, onCallEnd]);

  return {
    isMuted,
    isLoading,
    handleHold,
    handleResume,
    handleMute,
    handleEnd,
  };
}
